import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { customerAPI, teamviewerAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import { MonitorSmartphone, Plus, Pencil, Trash2, Check, X, Search, DownloadCloud } from "lucide-react";
import { toast } from "sonner";

export function Teamviewer() {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [newEntry, setNewEntry] = useState({ computer_name: "", teamviewer_id: "" });
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ computer_name: "", teamviewer_id: "" });
  const [importing, setImporting] = useState(false);

  const handleImportFromAssets = async () => {
    if (!window.confirm("Import all assets with a TeamViewer ID into the Teamviewer lists? Existing entries are kept (no duplicates).")) return;
    setImporting(true);
    try {
      const res = await teamviewerAPI.importFromAssets();
      toast.success(res.data.message);
      if (selectedCustomer) loadEntries(selectedCustomer.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await customerAPI.getAll();
        setCustomers(res.data.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        toast.error("Failed to load customers");
      }
    };
    load();
  }, []);

  const loadEntries = async (customerId) => {
    setLoadingEntries(true);
    try {
      const res = await teamviewerAPI.getByCustomer(customerId);
      setEntries(res.data);
    } catch (err) {
      toast.error("Failed to load TeamViewer entries");
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEditingId(null);
    setNewEntry({ computer_name: "", teamviewer_id: "" });
    loadEntries(customer.id);
  };

  const handleAdd = async () => {
    if (!newEntry.computer_name.trim() || !newEntry.teamviewer_id.trim()) {
      toast.error("Enter both Computer Name and TeamViewer ID");
      return;
    }
    setAdding(true);
    try {
      await teamviewerAPI.create({
        customer_id: selectedCustomer.id,
        computer_name: newEntry.computer_name.trim(),
        teamviewer_id: newEntry.teamviewer_id.trim(),
      });
      toast.success("Entry added");
      setNewEntry({ computer_name: "", teamviewer_id: "" });
      loadEntries(selectedCustomer.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add entry");
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditValues({ computer_name: entry.computer_name, teamviewer_id: entry.teamviewer_id });
  };

  const handleSaveEdit = async (entryId) => {
    if (!editValues.computer_name.trim() || !editValues.teamviewer_id.trim()) {
      toast.error("Both fields are required");
      return;
    }
    try {
      await teamviewerAPI.update(entryId, {
        computer_name: editValues.computer_name.trim(),
        teamviewer_id: editValues.teamviewer_id.trim(),
      });
      toast.success("Entry updated");
      setEditingId(null);
      loadEntries(selectedCustomer.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update entry");
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.computer_name}" (${entry.teamviewer_id})?`)) return;
    try {
      await teamviewerAPI.delete(entry.id);
      toast.success("Entry deleted");
      loadEntries(selectedCustomer.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete entry");
    }
  };

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="teamviewer-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" data-testid="teamviewer-title">
            <MonitorSmartphone className="w-7 h-7 text-blue-600" />
            Teamviewer
          </h1>
          <p className="text-slate-600 mt-1">Computer names and TeamViewer IDs per customer</p>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={handleImportFromAssets} disabled={importing} data-testid="teamviewer-import-button">
            <DownloadCloud className="w-4 h-4 mr-2" />
            {importing ? "Importing..." : "Import from Assets"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Entries table (left / main) */}
        <Card className="lg:col-span-2 order-2 lg:order-1">
          <CardHeader>
            <CardTitle className="text-base">
              {selectedCustomer ? `${selectedCustomer.name} — TeamViewer IDs` : "TeamViewer IDs"}
            </CardTitle>
            <CardDescription>
              {selectedCustomer
                ? "Add, edit or delete computers for this customer"
                : "Choose a customer from the list on the right to get started"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedCustomer ? (
              <div className="py-16 text-center text-slate-500" data-testid="teamviewer-empty-state">
                <MonitorSmartphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p>Select a customer to view their computers and TeamViewer IDs.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Add new row */}
                <div className="flex flex-col sm:flex-row gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <Input
                    placeholder="Computer Name"
                    value={newEntry.computer_name}
                    onChange={(e) => setNewEntry({ ...newEntry, computer_name: e.target.value })}
                    data-testid="teamviewer-new-computer-input"
                  />
                  <Input
                    placeholder="TeamViewer ID"
                    value={newEntry.teamviewer_id}
                    onChange={(e) => setNewEntry({ ...newEntry, teamviewer_id: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                    data-testid="teamviewer-new-id-input"
                  />
                  <Button onClick={handleAdd} disabled={adding} className="shrink-0" data-testid="teamviewer-add-button">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>

                {loadingEntries ? (
                  <p className="text-center py-8 text-slate-500">Loading...</p>
                ) : entries.length === 0 ? (
                  <p className="text-center py-8 text-slate-500" data-testid="teamviewer-no-entries">
                    No computers added yet for {selectedCustomer.name}.
                  </p>
                ) : (
                  <Table data-testid="teamviewer-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Computer Name</TableHead>
                        <TableHead>Teamviewer Id</TableHead>
                        <TableHead className="w-24 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={entry.id} data-testid={`teamviewer-row-${entry.id}`}>
                          {editingId === entry.id ? (
                            <>
                              <TableCell>
                                <Input
                                  value={editValues.computer_name}
                                  onChange={(e) => setEditValues({ ...editValues, computer_name: e.target.value })}
                                  className="h-8"
                                  data-testid={`teamviewer-edit-computer-${entry.id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={editValues.teamviewer_id}
                                  onChange={(e) => setEditValues({ ...editValues, teamviewer_id: e.target.value })}
                                  className="h-8"
                                  data-testid={`teamviewer-edit-id-${entry.id}`}
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => handleSaveEdit(entry.id)}
                                    data-testid={`teamviewer-save-${entry.id}`}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => setEditingId(null)}
                                    data-testid={`teamviewer-cancel-edit-${entry.id}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="font-medium">{entry.computer_name}</TableCell>
                              <TableCell className="font-mono">{entry.teamviewer_id}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => startEdit(entry)}
                                    data-testid={`teamviewer-edit-${entry.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDelete(entry)}
                                    data-testid={`teamviewer-delete-${entry.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer list (right) */}
        <Card className="order-1 lg:order-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Customers</CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search customers..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="teamviewer-customer-search"
              />
            </div>
          </CardHeader>
          <CardContent className="max-h-[560px] overflow-y-auto space-y-1 pt-0" data-testid="teamviewer-customer-list">
            {filteredCustomers.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No customers found</p>
            )}
            {filteredCustomers.map((customer) => (
              <button
                key={customer.id}
                onClick={() => handleSelectCustomer(customer)}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                  selectedCustomer?.id === customer.id
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-slate-700 hover:bg-slate-50"
                )}
                data-testid={`teamviewer-customer-${customer.id}`}
              >
                {customer.name}
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
